/**
 * UserPermissionsEditor (TEAM-PERMS) — the per-user access editor, shared by the
 * Team drawer (Seth's home for it) and Settings → Users & Roles so there's ONE
 * permissions system, not two. Model: pick a ROLE for the typical permissions
 * (the baseline), then fine-tune individual capabilities for THIS person
 * (tri-state: follow the role / always allow / never), and "Copy permissions
 * from another user" to clone someone's exact access in one click.
 *
 * Storage: app_user.role_key (baseline) + grant_extra[] / deny[] (the per-user
 * deltas). Effective = roleCaps + grant_extra − deny. Admin holds everything.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Check, X, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { capabilitiesByModule, type Capability } from "@/lib/permissions/catalog";

export interface AppUser {
  user_id: string;
  email: string | null;
  empno: string | null;
  role_key: string;
  grant_extra: string[] | null;
  deny: string[] | null;
}
export interface AppRole {
  key: string;
  name: string;
  description: string | null;
  is_admin: boolean;
  capabilities: string[] | null;
}

export function useAllAppUsers() {
  return useQuery({
    queryKey: ["app_users"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<AppUser[]> => {
      const { data, error } = await supabase
        .from("app_user")
        .select("user_id, email, empno, role_key, grant_extra, deny")
        .order("email");
      if (error) throw error;
      return (data ?? []) as AppUser[];
    },
  });
}

export function useRolesFull() {
  return useQuery({
    queryKey: ["app_roles"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<AppRole[]> => {
      const { data, error } = await supabase
        .from("app_role")
        .select("key, name, description, is_admin, capabilities")
        .order("is_admin", { ascending: false })
        .order("key");
      if (error) throw error;
      return (data ?? []) as AppRole[];
    },
  });
}

export function useSaveAppUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<AppUser> & { user_id: string }) => {
      const { data: auth } = await supabase.auth.getUser();
      const { user_id, ...patch } = v;
      const { error } = await supabase
        .from("app_user")
        .update({ ...patch, updated_by: auth?.user?.email ?? null, updated_at: new Date().toISOString() })
        .eq("user_id", user_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["app_users"] });
      qc.invalidateQueries({ queryKey: ["app_users_link"] });
    },
  });
}

export function UserPermissionsEditor({ userId }: { userId: string }) {
  const { user: me } = useAuth();
  const { data: users } = useAllAppUsers();
  const { data: roles } = useRolesFull();
  const save = useSaveAppUser();
  const [copyOpen, setCopyOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const u = (users ?? []).find((x) => x.user_id === userId) ?? null;
  if (!u || !roles) return <p className="text-xs text-slate-400">Loading permissions…</p>;

  const isSelf = me?.id === userId;
  const role = roles.find((r) => r.key === u.role_key) ?? null;
  const others = (users ?? []).filter((x) => x.user_id !== userId && x.email);

  const copyFrom = (srcId: string) => {
    const src = (users ?? []).find((x) => x.user_id === srcId);
    if (!src) return;
    save.mutate(
      { user_id: userId, role_key: src.role_key, grant_extra: src.grant_extra ?? [], deny: src.deny ?? [] },
      { onSuccess: () => { setCopied(true); setCopyOpen(false); setTimeout(() => setCopied(false), 2000); } },
    );
  };

  return (
    <div className="space-y-3">
      {/* Role baseline */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Role
          <InfoTip label="How roles and extras work">Pick a role for the typical permissions, then fine-tune individual ones below.</InfoTip>
        </span>
        {roles.map((r) => {
          const active = u.role_key === r.key;
          const locked = isSelf && role?.is_admin && !r.is_admin; // can't remove your own admin
          return (
            <button
              key={r.key}
              type="button"
              disabled={locked || save.isPending}
              title={locked ? "You can't remove your own admin access" : r.description ?? undefined}
              onClick={() => !active && save.mutate({ user_id: userId, role_key: r.key })}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40",
                active ? "border-brand-300 bg-brand-50 text-brand-800" : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
              )}
            >
              {r.name}
            </button>
          );
        })}
      </div>

      {/* Copy permissions from another user */}
      {others.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {!copyOpen ? (
            <button
              type="button"
              onClick={() => setCopyOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              <Copy className="h-3.5 w-3.5" /> Copy permissions from…
            </button>
          ) : (
            <select
              autoFocus
              value=""
              onChange={(e) => { if (e.target.value) copyFrom(e.target.value); }}
              onBlur={() => setCopyOpen(false)}
              className="h-8 rounded-lg border border-hairline bg-white px-2 text-xs text-slate-600 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            >
              <option value="">Copy exact permissions from…</option>
              {others.map((o) => <option key={o.user_id} value={o.user_id}>{o.email}</option>)}
            </select>
          )}
          {copied && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600"><Check className="h-3 w-3" /> Copied — exact match</span>}
        </div>
      )}

      {/* Per-user fine-tuning (admins hold everything) */}
      {role?.is_admin ? (
        <p className="text-[11px] text-slate-400">Admin holds every capability automatically, including ones added later.</p>
      ) : (
        <Exceptions u={u} role={role} pending={save.isPending} onSave={(patch) => save.mutate({ user_id: userId, ...patch })} />
      )}
      {save.isError && (
        <p className="text-[11px] font-medium text-rose-600">That change didn&apos;t save — the buttons show the previous state. Try again.</p>
      )}
    </div>
  );
}

/**
 * SETTINGS-ACCESS-01 (Seth 2026-09-03: "counterintuitive and messy/over-
 * complicated"): the 60-row tri-state grid is gone. A person follows their
 * role; "Also allow" and "Never" are short chip lists with a searchable
 * picker, and the full effective list is one fold away for auditing.
 */
function Exceptions({
  u, role, onSave, pending,
}: {
  u: AppUser;
  role: AppRole | null;
  onSave: (p: { grant_extra: string[]; deny: string[] }) => void;
  pending: boolean;
}) {
  const byModule = capabilitiesByModule();
  const roleCaps = new Set(role?.capabilities ?? []);
  const grant = new Set(u.grant_extra ?? []);
  const deny = new Set(u.deny ?? []);
  const byKey = new Map<string, Capability>();
  for (const caps of Object.values(byModule)) for (const c of caps) byKey.set(c.key, c);
  const labelOf = (k: string) => byKey.get(k)?.label ?? k;
  const [showAll, setShowAll] = useState(false);
  const [picker, setPicker] = useState<"allow" | "never" | null>(null);

  const add = (kind: "allow" | "never", k: string) => {
    const g = new Set(grant); const d = new Set(deny);
    g.delete(k); d.delete(k);
    if (kind === "allow") g.add(k); else d.add(k);
    onSave({ grant_extra: [...g], deny: [...d] });
    setPicker(null);
  };
  const remove = (k: string) => {
    const g = new Set(grant); const d = new Set(deny);
    g.delete(k); d.delete(k);
    onSave({ grant_extra: [...g], deny: [...d] });
  };
  const effective = new Set<string>([...roleCaps, ...grant]);
  for (const k of deny) effective.delete(k);
  // Only offer what would change something: Also allow = not already from the role; Never = currently effective.
  const allowChoices = Object.entries(byModule).map(([m, caps]) => [m, caps.filter((c) => !roleCaps.has(c.key) && !grant.has(c.key))] as const).filter(([, caps]) => caps.length);
  const neverChoices = Object.entries(byModule).map(([m, caps]) => [m, caps.filter((c) => effective.has(c.key) && !deny.has(c.key))] as const).filter(([, caps]) => caps.length);

  return (
    <div className="space-y-3">
      <ChipList
        title="Also allow" tone="allow" hint={`On top of the ${role?.name ?? "role"} role`}
        items={[...grant].map((k) => ({ key: k, label: labelOf(k) }))} pending={pending}
        onRemove={remove} onAdd={() => setPicker(picker === "allow" ? null : "allow")} adding={picker === "allow"}
      />
      {picker === "allow" && <CapabilityPicker groups={allowChoices} tone="allow" onPick={(k) => add("allow", k)} onClose={() => setPicker(null)} />}
      <ChipList
        title="Never" tone="never" hint="Taken away even though the role allows it"
        items={[...deny].map((k) => ({ key: k, label: labelOf(k) }))} pending={pending}
        onRemove={remove} onAdd={() => setPicker(picker === "never" ? null : "never")} adding={picker === "never"}
      />
      {picker === "never" && <CapabilityPicker groups={neverChoices} tone="never" onPick={(k) => add("never", k)} onClose={() => setPicker(null)} />}
      <div>
        <button type="button" onClick={() => setShowAll((v) => !v)} className="text-[11.5px] font-medium text-slate-500 hover:text-slate-800">
          {showAll ? "Hide" : "Show"} everything this person can do ({effective.size})
        </button>
        {showAll && (
          <div className="mt-2 grid gap-x-6 gap-y-2 rounded-lg border border-hairline bg-white p-3 sm:grid-cols-2">
            {Object.entries(byModule).map(([module, caps]) => {
              const on = caps.filter((c) => effective.has(c.key));
              if (!on.length) return null;
              return (
                <div key={module}>
                  <div className="mb-0.5 text-[11px] font-semibold text-slate-500">{module}</div>
                  <ul className="space-y-0.5">
                    {on.map((c) => (
                      <li key={c.key} className="flex items-center gap-1.5 text-xs text-slate-700">
                        <Check className="h-3 w-3 shrink-0 text-emerald-500" />
                        <span className="min-w-0 truncate" title={c.key}>{c.label}</span>
                        {grant.has(c.key) && <span className="shrink-0 rounded bg-emerald-50 px-1 text-[10px] font-semibold text-emerald-700">extra</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ChipList({ title, hint, tone, items, pending, onRemove, onAdd, adding }: {
  title: string; hint: string; tone: "allow" | "never"; items: { key: string; label: string }[]; pending: boolean;
  onRemove: (k: string) => void; onAdd: () => void; adding: boolean;
}) {
  const chip = tone === "allow" ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200" : "bg-rose-50 text-rose-800 ring-1 ring-rose-200";
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{title}</span>
        <span className="text-[11px] text-slate-400">{hint}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {items.length === 0 && <span className="text-xs text-slate-400">{tone === "allow" ? "Nothing extra" : "Nothing taken away"}</span>}
        {items.map((it) => (
          <span key={it.key} className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium", chip)} title={it.key}>
            {it.label}
            <button type="button" disabled={pending} onClick={() => onRemove(it.key)} className="rounded-full p-0.5 opacity-60 hover:opacity-100" aria-label={`Remove ${it.label}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        <button type="button" disabled={pending} onClick={onAdd}
          className={cn("inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-[11.5px] font-medium transition-colors", adding ? "border-slate-400 bg-slate-100 text-slate-700" : "border-hairline text-slate-500 hover:border-slate-400 hover:text-slate-800")}>
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
    </div>
  );
}

function CapabilityPicker({ groups, tone, onPick, onClose }: {
  groups: readonly (readonly [string, Capability[]])[]; tone: "allow" | "never"; onPick: (k: string) => void; onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const t = q.trim().toLowerCase();
  const filtered = groups.map(([m, caps]) => [m, t ? caps.filter((c) => `${c.label} ${c.key} ${m}`.toLowerCase().includes(t)) : caps] as const).filter(([, caps]) => caps.length);
  return (
    <div className="rounded-lg border border-hairline bg-white p-2 shadow-sm">
      <div className="flex items-center gap-2">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
          placeholder={tone === "allow" ? "Search — e.g. approve time off, manager view, pay" : "Search what to take away"}
          className="h-8 flex-1 rounded-md border border-hairline bg-white px-2 text-xs text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close"><X className="h-3.5 w-3.5" /></button>
      </div>
      <div className="mt-2 max-h-56 space-y-2 overflow-y-auto pr-1">
        {filtered.length === 0 && <p className="px-1 py-2 text-xs text-slate-400">Nothing matches.</p>}
        {filtered.map(([module, caps]) => (
          <div key={module}>
            <div className="px-1 text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">{module}</div>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {caps.map((c) => (
                <button key={c.key} type="button" onClick={() => onPick(c.key)} title={c.key}
                  className={cn("rounded-full border px-2 py-0.5 text-left text-[11.5px] font-medium transition-colors", tone === "allow" ? "border-hairline bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50" : "border-hairline bg-white text-slate-700 hover:border-rose-300 hover:bg-rose-50")}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
